import { Box, TableCell, Tooltip, Typography } from '@mui/material'
import ToggleSwitch from 'components/Form/Toggle'
import usePermission from 'hooks/usePermission/usePermission'
import { useMemo } from 'react'
import { IHeader } from './IHeader'
import { useAppSelector } from 'store/useStoreHook'
import { RowDefinition } from './Rows/RowDefinition'
import { get } from 'lodash-es'

interface IProps<T> {
    itemKey: IHeader
    item: T
    rowOption?: RowDefinition<T>
    onToggleSwitch?: (key: string, item: T) => void
    width?: number
    editPermission?: number[]
    fixLeftNumber?: number
    disabledTooltip?: string
}

export function CellTable<T>({
    itemKey,
    item,
    rowOption,
    onToggleSwitch,
    width,
    editPermission = [],
    fixLeftNumber = 0,
    disabledTooltip,
}: IProps<T>) {
    const userData = useAppSelector((state) => state.auth.user)

    const { hasPermission } = usePermission()
    const toggleSwitch = useMemo(
        () => (
            <ToggleSwitch
                color="primary"
                size="small"
                disabled={
                    (rowOption?.disabledRow?.(item) ?? false) ||
                    (itemKey.disabled && itemKey.disabled(item)) ||
                    !hasPermission(editPermission)
                }
                onClick={(event) => {
                    event.stopPropagation()
                }}
                onChange={() => {
                    if (onToggleSwitch) {
                        if (typeof itemKey.value === 'string' || itemKey.name)
                            onToggleSwitch(itemKey.name || itemKey.value, item)
                        else
                            throw new Error(
                                'Undefined IHeader.name; onToggleSwitch callback required column name',
                            )
                    }
                }}
                checked={
                    typeof itemKey.value === 'string'
                        ? get(item, itemKey.value)
                        : itemKey.value(item)
                }
            />
        ),
        [
            editPermission,
            hasPermission,
            item,
            itemKey,
            onToggleSwitch,
            rowOption,
        ],
    )

    const value = useMemo(() => {
        if (itemKey.hidden && itemKey.hidden(item))
            if (get(item, 'id') === userData?.userId)
                return (
                    <Typography color="primary.light">this account</Typography>
                )
            else return null
        if (itemKey.switch)
            if (get(item, 'id') === userData?.userId) {
                return <Typography color="warning">this account</Typography>
            } else {
                const isDisabledByFunc =
                    itemKey.disabled && itemKey.disabled(item)

                if (isDisabledByFunc && disabledTooltip) {
                    return (
                        <Tooltip title={disabledTooltip}>
                            <Box>{toggleSwitch}</Box>
                        </Tooltip>
                    )
                }

                return toggleSwitch
            }

        if (typeof itemKey.value === 'string') {
            return get(item, itemKey.value)
        }

        return itemKey.value(item)
    }, [disabledTooltip, item, itemKey, toggleSwitch, userData?.userId])
    const itemStyle = useMemo(() => {
        if (typeof itemKey.itemStyle === 'object') return itemKey.itemStyle
        if (typeof itemKey.itemStyle === 'function')
            return itemKey.itemStyle(item)
    }, [item, itemKey])

    return (
        <TableCell
            width={width}
            sx={{
                position: itemKey.isSticky ? 'sticky' : 'static',
                left: 0,
                borderRight: itemKey.isSticky
                    ? (theme) => 'solid 2px ' + theme.palette.default.main
                    : undefined,
                ...(rowOption?.conditionStyle?.(item) ?? {}),
            }}
            style={{
                textAlign: itemKey.align ? itemKey.align : 'initial',
                left: itemKey.isSticky ? fixLeftNumber : 'initial',
                ...itemStyle,
            }}
            variant={itemKey.isSticky ? 'head' : 'body'}
        >
            {value}
        </TableCell>
    )
}
